import React, { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import heartLoading from "../../../assests/heartLoading.gif";
import { BusinessUserTable } from "./BusinessUserTable";

const parsePeriodWeekKey = (key) => {
  const match = key?.match(/^(\d{4})P(\d+)W(\d+)$/);
  if (!match) return null;
  return {
    year: match[1],
    period: `P${match[2].padStart(2, "0")}`,
    week: `W${match[3].padStart(2, "0")}`,
  };
};

const parseGraphData = (graphData) => {
  if (!graphData) return [];
  const keys = Object.keys(graphData);
  if (!keys.length) return [];
  const totalKeys = keys.filter((k) => k.startsWith("x")).length;
  const firstX = graphData["x1"];
  const isPeriodWeek = /^\d{4}P\d+W\d+$/.test(firstX || "");

  if (isPeriodWeek) {
    const aggregated = {};
    for (let i = 1; i <= totalKeys; i++) {
      const raw = graphData[`x${i}`];
      const value = graphData[`y${i}`] ?? 0;
      if (!raw) continue;
      aggregated[raw] = (aggregated[raw] || 0) + value;
    }
    return Object.entries(aggregated)
      .map(([key, count]) => {
        const parsed = parsePeriodWeekKey(key);
        return { date: key, count, ...parsed };
      })
      .sort((a, b) => {
        const [ay, ap, aw] = [a.year, parseInt(a.period.slice(1)), parseInt(a.week.slice(1))];
        const [by, bp, bw] = [b.year, parseInt(b.period.slice(1)), parseInt(b.week.slice(1))];
        return ay !== by ? ay - by : ap !== bp ? ap - bp : aw - bw;
      });
  } else {
    const dateMap = {};
    for (let i = 1; i <= totalKeys; i++) {
      const date = graphData[`x${i}`];
      const value = graphData[`y${i}`] ?? 0;
      if (!date) continue;
      dateMap[date] = (dateMap[date] || 0) + value;
    }
    return Object.entries(dateMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }
};

const formatXLabel = (dateStr) => {
  if (/^\d{4}P\d+W\d+$/.test(dateStr)) {
    const p = parsePeriodWeekKey(dateStr);
    return p ? p.week : dateStr;
  }
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatTooltipDate = (dateStr) => {
  if (/^\d{4}P\d+W\d+$/.test(dateStr)) {
    const p = parsePeriodWeekKey(dateStr);
    return p ? `${p.year} ${p.period} ${p.week}` : dateStr;
  }
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    weekday: "short", year: "numeric", month: "short", day: "numeric",
  });
};

const buildGroupTicks = (data) => {
  const periods = [];
  const years = [];
  let lastPeriod = null, lastYear = null;
  data.forEach((d, i) => {
    if (d.period && d.period !== lastPeriod) {
      periods.push({ startIndex: i, label: d.period });
      lastPeriod = d.period;
    }
    if (d.year && d.year !== lastYear) {
      years.push({ startIndex: i, label: d.year });
      lastYear = d.year;
    }
  });
  const withEnd = (arr) =>
    arr.map((g, i) => ({
      ...g,
      endIndex: i + 1 < arr.length ? arr[i + 1].startIndex - 1 : data.length - 1,
    }));
  return { periods: withEnd(periods), years: withEnd(years) };
};

const ACCENT = "#6366F1";
const ACCENT_LIGHT = "#0D9488";
const TOOLTIP_COLOR = "#2dd4bf";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
      padding: "10px 16px", boxShadow: "0 4px 20px rgba(45,212,191,0.15)", minWidth: 160,
    }}>
      <p style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>{formatTooltipDate(label)}</p>
      <p style={{ color: TOOLTIP_COLOR, fontWeight: 700, fontSize: 20, margin: 0 }}>
        {payload[0].value.toLocaleString()}
        <span style={{ fontSize: 13, fontWeight: 400, color: "#6b7280", marginLeft: 5 }}>queries</span>
      </p>
    </div>
  );
};

const GroupedXAxis = ({ data, chartWidth, marginLeft, marginRight }) => {
  if (!data.length || !data[0].period) return null;
  const { periods, years } = buildGroupTicks(data);
  const n = data.length;
  const innerWidth = chartWidth - marginLeft - marginRight;
  const slotWidth = innerWidth / n;

  const groupX1 = (i) => marginLeft + i * slotWidth + 1;
  const groupX2 = (i) => marginLeft + (i + 1) * slotWidth - 1;
  const groupCX = (s, e) => (groupX1(s) + groupX2(e)) / 2;

  return (
    <svg
      viewBox={`0 0 ${chartWidth} 54`}
      width="100%"
      height={54}
      style={{ display: "block", marginTop: -2 }}
    >
      {periods.map((g) => {
        const x1 = groupX1(g.startIndex);
        const x2 = groupX2(g.endIndex);
        const cx = groupCX(g.startIndex, g.endIndex);
        return (
          <g key={`p-${g.label}-${g.startIndex}`}>
            <line x1={x1} x2={x2} y1={6} y2={6} stroke="#94a3b8" strokeWidth={1} />
            <line x1={x1} x2={x1} y1={2} y2={11} stroke="#94a3b8" strokeWidth={1} />
            <line x1={x2} x2={x2} y1={2} y2={11} stroke="#94a3b8" strokeWidth={1} />
            <text x={cx} y={22} textAnchor="middle" fontSize={11} fill="#64748b" fontWeight={500}>
              {g.label}
            </text>
          </g>
        );
      })}
      {years.map((g) => {
        const x1 = groupX1(g.startIndex);
        const x2 = groupX2(g.endIndex);
        const cx = groupCX(g.startIndex, g.endIndex);
        return (
          <g key={`y-${g.label}-${g.startIndex}`}>
            <line x1={x1} x2={x2} y1={31} y2={31} stroke="#94a3b8" strokeWidth={1} />
            <line x1={x1} x2={x1} y1={27} y2={36} stroke="#94a3b8" strokeWidth={1} />
            <line x1={x2} x2={x2} y1={27} y2={36} stroke="#94a3b8" strokeWidth={1} />
            <text x={cx} y={50} textAnchor="middle" fontSize={11} fill="#374151" fontWeight={600}>
              {g.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export const ReportUsagetrend = ({
  usageData,
  loading,
  sortBy = "",
  sortOrder = "desc",
  columnSearch = {},
  onSortChange,
  onSearchChange,
  hasInteracted,
  accumulatedUsers = [],
  loadMoreBusiness,
  businessHasMore,
  isFetchingMoreBusiness,
}) => {
  const graphData = usageData?.graph_data || {};
  const pagination = usageData?.pagination || {};

  const allData = useMemo(() => parseGraphData(graphData || {}), [graphData]);
  const isPeriodWeek = allData.length > 0 && !!allData[0].period;

  const maxCount = useMemo(() => Math.max(...allData.map((d) => d.count), 1), [allData]);
  const totalQueries = useMemo(() => allData.reduce((sum, d) => sum + d.count, 0), [allData]);
  const avgQueries = allData.length ? (totalQueries / allData.length).toFixed(1) : 0;
  const peakDay = useMemo(
    () => allData.reduce((max, d) => (d.count > (max?.count ?? 0) ? d : max), null),
    [allData]
  );

  // ✅ FIX 1: Split comma-separated emails into individual array items
  const normalizedUsers = useMemo(() => {
    return accumulatedUsers.map((user) => ({
      ...user,
      name: (user.name || []).flatMap((nameStr) =>
        typeof nameStr === "string"
          ? nameStr.split(",").map((n) => n.trim()).filter(Boolean)
          : []
      ),
    }));
  }, [accumulatedUsers]);

  const kpiCards = isPeriodWeek
    ? [
        { label: "Total Queries", value: totalQueries.toLocaleString() },
        { label: "Weekly Avg", value: avgQueries },
        {
          label: "Peak Week",
          value: peakDay
            ? `${peakDay.count.toLocaleString()} on ${peakDay.period} ${peakDay.week}`
            : "—",
        },
      ]
    : [
        { label: "Total Queries", value: totalQueries.toLocaleString() },
        { label: "Daily Avg", value: avgQueries },
        {
          label: "Peak Day",
          value: peakDay
            ? `${peakDay.count.toLocaleString()} on ${formatXLabel(peakDay.date)}`
            : "—",
        },
      ];

  const [chartWidth, setChartWidth] = React.useState(600);
  const chartWrapperRef = React.useRef(null);

  React.useEffect(() => {
    if (!chartWrapperRef.current) return;
    const updateWidth = () => {
      if (chartWrapperRef.current) {
        setChartWidth(chartWrapperRef.current.getBoundingClientRect().width);
      }
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(chartWrapperRef.current);
    return () => ro.disconnect();
  }, [allData]);

  const MARGIN = { top: 10, right: 10, left: 45, bottom: isPeriodWeek ? 10 : 40 };
  const labelInterval = Math.max(1, Math.floor(allData.length / 10));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-2xl text-center">
            Loading usage trend...
          </span>
        </div>
      </div>
    );
  }

  if (!graphData || Object.keys(graphData).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-gray-400">
        <span className="text-sm">No usage data available for this asset.</span>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Usage Trend</h2>
          <p className="text-sm text-gray-500 mt-0.5">Query volume over time for this data asset</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {kpiCards.map((kpi) => (
            <div key={kpi.label} className="bg-[#EDE9FE] rounded-xl px-4 py-3 border border-blue-100">
              <p className="text-xs text-gray-500 mb-0.5">{kpi.label}</p>
              <p className="text-base font-bold text-[#bb1ccc] truncate">{kpi.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" style={{ minHeight: 320 }}>
          {allData.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
              No data available.
            </div>
          ) : (
            <div ref={chartWrapperRef}>
              <ResponsiveContainer width="100%" height={isPeriodWeek ? 260 : 300}>
                <BarChart data={allData} margin={MARGIN} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatXLabel}
                    interval={isPeriodWeek ? 0 : labelInterval - 1}
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    angle={isPeriodWeek ? 0 : -35}
                    textAnchor={isPeriodWeek ? "middle" : "end"}
                    height={isPeriodWeek ? 24 : 50}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                    width={45}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f0f4ff" }} />
                  <Bar
                    dataKey="count"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    label={{ position: "top", fontSize: 10, fill: "#374151" }}
                  >
                    {allData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.count === maxCount ? ACCENT_LIGHT : ACCENT}
                        fillOpacity={0.75 + 0.25 * (entry.count / maxCount)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {isPeriodWeek && (
                <GroupedXAxis
                  data={allData}
                  chartWidth={chartWidth}
                  marginLeft={MARGIN.left}
                  marginRight={MARGIN.right}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ✅ FIX 1 applied: pass normalizedUsers instead of accumulatedUsers */}
      <BusinessUserTable
        businessUser={normalizedUsers}
        sortBy={sortBy}
        sortOrder={sortOrder}
        columnSearch={columnSearch}
        onSortChange={onSortChange}
        onSearchChange={onSearchChange}
        totalRecords={pagination?.total_records}
        loadMore={loadMoreBusiness}
        hasMore={businessHasMore}
        isFetchingMore={isFetchingMoreBusiness}
      />
    </>
  );
};